<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import DatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';

const props = defineProps({
  availability: { type: Object, default: null },
  upcomingHolidays: { type: Array, default: () => [] },
  connected: { type: Boolean, default: false },
});
const emit = defineEmits(['saved']);

// Segunda a domingo, com a chave de Date#getDay/Date#wday do backend.
const WEEKDAYS = [
  { key: '1', label: 'Segunda' },
  { key: '2', label: 'Terça' },
  { key: '3', label: 'Quarta' },
  { key: '4', label: 'Quinta' },
  { key: '5', label: 'Sexta' },
  { key: '6', label: 'Sábado' },
  { key: '0', label: 'Domingo' },
];
const SLOT_OPTIONS = [15, 20, 30, 40, 45, 60, 90, 120].map(value => ({
  value,
  label:
    value < 60 ? `${value} minutos` : `${value / 60} h`.replace('.5', ',5'),
}));
const NOTICE_OPTIONS = [
  { value: 0, label: 'Sem antecedência' },
  { value: 30, label: '30 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 240, label: '4 horas' },
  { value: 1440, label: '1 dia' },
  { value: 2880, label: '2 dias' },
];
const MAX_BLOCKED_DATES = 200;

const clone = value => JSON.parse(JSON.stringify(value ?? {}));
const defaultWeekly = () =>
  Object.fromEntries(
    WEEKDAYS.map(({ key }) => [
      key,
      { enabled: !['0', '6'].includes(key), open: '08:00', close: '18:00' },
    ])
  );
const form = reactive({
  ai_scheduling_enabled: false,
  ai_google_meet_enabled: false,
  closed: false,
  closed_message: '',
  timezone: 'America/Sao_Paulo',
  slot_minutes: 60,
  min_notice_minutes: 120,
  horizon_days: 30,
  weekly: defaultWeekly(),
  lunch: { enabled: true, start: '12:00', end: '13:00' },
  blocked_dates: [],
  block_national_holidays: true,
  holiday_exceptions: [],
  inbox_scope: 'all',
  inbox_ids: [],
});
const isSaving = ref(false);

// Feriados nacionais: bloqueados por padrão; um clique marca que a empresa atende.
const isHolidayOpen = holiday => form.holiday_exceptions.includes(holiday.date);
const toggleHoliday = holiday => {
  form.holiday_exceptions = isHolidayOpen(holiday)
    ? form.holiday_exceptions.filter(date => date !== holiday.date)
    : [...form.holiday_exceptions, holiday.date].sort();
};
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxQuery = ref('');

onMounted(() => {
  if (!inboxes.value?.length) store.dispatch('inboxes/get');
});

const sortedInboxes = computed(() =>
  [...(inboxes.value || [])].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), 'pt-BR')
  )
);
const filteredInboxes = computed(() => {
  const query = inboxQuery.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return sortedInboxes.value;
  return sortedInboxes.value.filter(inbox =>
    String(inbox.name).toLocaleLowerCase('pt-BR').includes(query)
  );
});
const selectedInboxIds = computed(() => new Set(form.inbox_ids.map(Number)));
const isInboxSelected = inbox => selectedInboxIds.value.has(Number(inbox.id));
const toggleInbox = inbox => {
  const id = Number(inbox.id);
  form.inbox_ids = isInboxSelected(inbox)
    ? form.inbox_ids.filter(value => Number(value) !== id)
    : [...form.inbox_ids, id];
};
const selectVisibleInboxes = () => {
  const ids = new Set(form.inbox_ids.map(Number));
  filteredInboxes.value.forEach(inbox => ids.add(Number(inbox.id)));
  form.inbox_ids = [...ids];
};
const clearInboxes = () => {
  form.inbox_ids = [];
};
const inboxScopeOptions = [
  {
    value: 'all',
    title: 'Todas as caixas',
    description: 'A IA usa a agenda em qualquer caixa com assistente.',
    icon: 'i-hugeicons-inbox',
  },
  {
    value: 'selected',
    title: 'Somente as selecionadas',
    description: 'A agenda fica disponível só nas caixas escolhidas.',
    icon: 'i-hugeicons-checkmark-square-02',
  },
];
const blockReason = ref('');
const rangeType = ref(DATE_RANGE_TYPES.CUSTOM_RANGE);
const blockRange = ref([new Date(), new Date()]);

watch(
  () => props.availability,
  value => {
    if (value)
      Object.assign(form, clone(value), {
        weekly: { ...defaultWeekly(), ...clone(value.weekly) },
      });
  },
  { immediate: true }
);

const toIsoDate = date => {
  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Máscara HH:MM para os horários, sem depender do formato 12h do navegador.
const maskTime = value => {
  let digits = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 5);
  // Hora 0–23 e minuto 0–59: "9" vira "09", "29" vira "23" e "7" nos minutos vira "5".
  if (digits && digits[0] > '2') digits = `0${digits}`;
  digits = digits.slice(0, 4);
  if (digits.length >= 2 && Number(digits.slice(0, 2)) > 23) {
    digits = `23${digits.slice(2)}`;
  }
  if (digits.length >= 3 && digits[2] > '5') {
    digits = `${digits.slice(0, 2)}5${digits.slice(3)}`;
  }
  return digits.length > 2
    ? `${digits.slice(0, 2)}:${digits.slice(2)}`
    : digits;
};

// Completa horários parciais ao sair do campo: "8" → "08:00", "830" → "08:30".
const completeTime = value => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 2) return maskTime(`${digits.padStart(2, '0')}00`);
  if (digits.length === 3) return maskTime(`0${digits}`);
  return maskTime(digits);
};

// O texto do campo também é reescrito: sem isso, um valor rejeitado pela
// máscara continuaria visível no input.
const setTime = (target, key, event, complete = false) => {
  const value = (complete ? completeTime : maskTime)(event.target.value);
  target[key] = value;
  if (event.target.value !== value) event.target.value = value;
};

const formatDate = iso => {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

const blockRangeLabel = computed(() => {
  const [start, end] = blockRange.value ?? [];
  if (!start || !end) return 'Escolha o dia ou o período';
  const first = formatDate(toIsoDate(start));
  const last = formatDate(toIsoDate(end));
  return first === last ? first : `${first} a ${last}`;
});

const openDaysCount = computed(
  () => WEEKDAYS.filter(day => form.weekly[day.key]?.enabled).length
);

const statusText = computed(() => {
  if (!props.connected)
    return 'Conecte e ative o Google Agenda para a IA usar a agenda.';
  if (!form.ai_scheduling_enabled)
    return 'A IA não consulta nem marca horários.';
  if (form.closed)
    return 'Agenda fechada: a IA não oferece nem marca horários.';
  if (form.inbox_scope === 'selected') {
    return `A IA oferece horários livres em ${openDaysCount.value} dia(s) da semana, em ${form.inbox_ids.length} caixa(s) selecionada(s).`;
  }
  return `A IA oferece horários livres em ${openDaysCount.value} dia(s) da semana.`;
});

const addBlockedPeriod = () => {
  const [start, end] = blockRange.value ?? [];
  if (!start || !end) return;

  const known = new Set(form.blocked_dates.map(entry => entry.date));
  const cursor = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  );
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const added = [];
  while (cursor <= last) {
    const iso = toIsoDate(cursor);
    if (!known.has(iso))
      added.push({ date: iso, reason: blockReason.value.trim() });
    cursor.setDate(cursor.getDate() + 1);
  }

  if (form.blocked_dates.length + added.length > MAX_BLOCKED_DATES) {
    useAlert(`Cadastre no máximo ${MAX_BLOCKED_DATES} datas bloqueadas.`);
    return;
  }
  form.blocked_dates = [...form.blocked_dates, ...added].sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  blockReason.value = '';
};

const removeBlockedDate = date => {
  form.blocked_dates = form.blocked_dates.filter(entry => entry.date !== date);
};

const errorMessage = error =>
  error?.response?.data?.error ||
  'Não foi possível salvar a agenda. Tente novamente em instantes.';

const save = async () => {
  if (form.inbox_scope === 'selected' && !form.inbox_ids.length) {
    useAlert('Selecione ao menos uma caixa de entrada.');
    return;
  }
  isSaving.value = true;
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.updateAvailability(
      clone(form)
    );
    emit('saved', data);
    useAlert('Agenda da IA salva.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <form
    class="mt-6 grid max-w-3xl gap-6 rounded-xl border border-n-weak bg-n-card p-6"
    data-testid="calendar-availability"
    @submit.prevent="save"
  >
    <header class="flex items-start gap-4">
      <div
        class="grid size-12 shrink-0 place-content-center rounded-xl border border-n-weak bg-n-alpha-2 text-n-brand"
      >
        <span
          class="i-hugeicons-calendar-check-in-01 size-6"
          aria-hidden="true"
        />
      </div>
      <div class="min-w-0">
        <h3 class="mb-1 text-lg font-semibold text-n-slate-12">Agenda da IA</h3>
        <p class="mb-0 text-sm text-n-slate-11">{{ statusText }}</p>
      </div>
    </header>

    <section class="grid gap-3">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            Permitir que a IA consulte e marque horários
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            A IA só oferece horários dentro do expediente e livres no Google
            Agenda. Antes de marcar, o horário é conferido de novo.
          </p>
        </div>
        <Switch v-model="form.ai_scheduling_enabled" data-testid="ai-toggle" />
      </div>

      <div
        v-if="form.ai_scheduling_enabled"
        class="flex items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-1 p-3"
      >
        <div class="min-w-0">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            Criar link do Google Meet
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            Cada horário marcado pela IA ganha uma sala do Google Meet, e o link
            vai para o cliente na confirmação do agendamento.
          </p>
        </div>
        <Switch
          v-model="form.ai_google_meet_enabled"
          data-testid="ai-meet-toggle"
        />
      </div>

      <div
        class="flex items-start justify-between gap-4 rounded-lg border p-3"
        :class="
          form.closed
            ? 'border-n-ruby-6 bg-n-ruby-2'
            : 'border-n-weak bg-n-solid-1'
        "
      >
        <div class="min-w-0">
          <p class="mb-0 text-sm font-medium text-n-slate-12">Fechar agenda</p>
          <p class="mb-0 text-xs text-n-slate-11">
            Suspende novos agendamentos pela IA, sem apagar o que já está
            marcado.
          </p>
        </div>
        <Switch v-model="form.closed" data-testid="closed-toggle" />
      </div>
      <Input
        v-if="form.closed"
        v-model="form.closed_message"
        label="Mensagem para a IA explicar ao cliente (opcional)"
        placeholder="Ex.: Estamos com a agenda fechada até 10/01."
      />
    </section>

    <section class="grid gap-3" data-testid="inbox-scope">
      <div>
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          Caixas de entrada
        </h4>
        <p class="mb-0 text-xs text-n-slate-11">
          Escolha onde a IA pode consultar e marcar horários.
        </p>
      </div>
      <div class="grid gap-2 sm:grid-cols-2" role="radiogroup">
        <button
          v-for="option in inboxScopeOptions"
          :key="option.value"
          type="button"
          role="radio"
          :aria-checked="form.inbox_scope === option.value"
          class="flex items-start gap-3 rounded-xl border border-solid p-3 text-left transition-colors"
          :class="
            form.inbox_scope === option.value
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak hover:bg-n-alpha-2'
          "
          :data-testid="`inbox-scope-${option.value}`"
          @click="form.inbox_scope = option.value"
        >
          <span
            class="mt-0.5 size-4 shrink-0"
            :class="[
              option.icon,
              form.inbox_scope === option.value
                ? 'text-n-brand'
                : 'text-n-slate-11',
            ]"
            aria-hidden="true"
          />
          <span class="min-w-0">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ option.title }}
            </span>
            <span class="block text-xs text-n-slate-11">
              {{ option.description }}
            </span>
          </span>
        </button>
      </div>

      <div
        v-if="form.inbox_scope === 'selected'"
        class="grid gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3"
      >
        <div class="flex flex-wrap items-center gap-2">
          <Input
            v-if="sortedInboxes.length > 6"
            v-model="inboxQuery"
            size="sm"
            placeholder="Buscar caixa"
            class="min-w-0 flex-1"
          />
          <span class="text-xs text-n-slate-11" data-testid="inbox-count">
            {{
              `${form.inbox_ids.length} de ${sortedInboxes.length} selecionada(s)`
            }}
          </span>
          <span class="ml-auto flex gap-1">
            <Button
              type="button"
              size="xs"
              color="slate"
              variant="ghost"
              label="Selecionar todas"
              @click="selectVisibleInboxes"
            />
            <Button
              type="button"
              size="xs"
              color="slate"
              variant="ghost"
              label="Limpar"
              @click="clearInboxes"
            />
          </span>
        </div>
        <ul class="m-0 grid max-h-64 list-none gap-1 overflow-y-auto p-0">
          <li v-for="inbox in filteredInboxes" :key="inbox.id">
            <label
              class="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-n-alpha-2"
              :data-testid="`inbox-option-${inbox.id}`"
            >
              <Checkbox
                :model-value="isInboxSelected(inbox)"
                @change="toggleInbox(inbox)"
              />
              <span
                class="grid size-7 shrink-0 place-items-center rounded-lg bg-n-alpha-2"
              >
                <ChannelIcon :inbox="inbox" colored class="size-4" />
              </span>
              <span class="min-w-0 truncate text-sm text-n-slate-12">
                {{ inbox.name }}
              </span>
            </label>
          </li>
          <li
            v-if="!filteredInboxes.length"
            class="px-2 py-3 text-xs text-n-slate-11"
          >
            Nenhuma caixa encontrada.
          </li>
        </ul>
      </div>
    </section>

    <section class="grid gap-2">
      <div class="mb-1">
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          Horário de abertura
        </h4>
        <p class="mb-0 text-xs text-n-slate-11">
          Horário de Brasília, no formato 24 horas.
        </p>
      </div>
      <div
        class="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-x-3 text-xs text-n-slate-11 sm:grid-cols-[2rem_7rem_minmax(0,17rem)]"
        aria-hidden="true"
      >
        <span />
        <span class="hidden sm:block">Dia</span>
        <span class="col-start-2 grid grid-cols-2 gap-2 sm:col-start-auto">
          <span>Abre</span>
          <span>Fecha</span>
        </span>
      </div>
      <div
        v-for="day in WEEKDAYS"
        :key="day.key"
        class="relative grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 sm:grid-cols-[2rem_7rem_minmax(0,17rem)]"
        :data-testid="`weekday-${day.key}`"
      >
        <Switch v-model="form.weekly[day.key].enabled" />
        <span class="text-sm text-n-slate-12">{{ day.label }}</span>
        <div
          v-if="form.weekly[day.key].enabled"
          class="col-start-2 grid grid-cols-2 gap-2 sm:col-start-auto"
        >
          <label :for="`open-${day.key}`" class="sr-only">
            {{ `Abre ${day.label}` }}
          </label>
          <Input
            :id="`open-${day.key}`"
            :model-value="form.weekly[day.key].open"
            size="sm"
            placeholder="08:00"
            @input="setTime(form.weekly[day.key], 'open', $event)"
            @blur="setTime(form.weekly[day.key], 'open', $event, true)"
          />
          <label :for="`close-${day.key}`" class="sr-only">
            {{ `Fecha ${day.label}` }}
          </label>
          <Input
            :id="`close-${day.key}`"
            :model-value="form.weekly[day.key].close"
            size="sm"
            placeholder="18:00"
            @input="setTime(form.weekly[day.key], 'close', $event)"
            @blur="setTime(form.weekly[day.key], 'close', $event, true)"
          />
        </div>
        <span
          v-else
          class="col-start-2 text-xs text-n-slate-11 sm:col-start-auto"
        >
          Fechado
        </span>
      </div>
    </section>

    <section class="grid gap-3">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            Horário de almoço
          </h4>
          <p class="mb-0 text-xs text-n-slate-11">
            Nenhum atendimento é marcado nesse intervalo.
          </p>
        </div>
        <Switch v-model="form.lunch.enabled" data-testid="lunch-toggle" />
      </div>
      <div
        v-if="form.lunch.enabled"
        class="grid grid-cols-2 gap-3 sm:max-w-[17rem]"
      >
        <Input
          :model-value="form.lunch.start"
          label="Início"
          size="sm"
          placeholder="12:00"
          @input="setTime(form.lunch, 'start', $event)"
          @blur="setTime(form.lunch, 'start', $event, true)"
        />
        <Input
          :model-value="form.lunch.end"
          label="Fim"
          size="sm"
          placeholder="13:00"
          @input="setTime(form.lunch, 'end', $event)"
          @blur="setTime(form.lunch, 'end', $event, true)"
        />
      </div>
    </section>

    <section class="grid gap-3">
      <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
        Regras dos horários
      </h4>
      <div class="grid gap-3 sm:grid-cols-3">
        <label class="grid content-start gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">Duração</span>
          <Select
            v-model="form.slot_minutes"
            class="!w-full"
            :options="SLOT_OPTIONS"
          />
        </label>
        <label class="grid content-start gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">
            Antecedência mínima
          </span>
          <Select
            v-model="form.min_notice_minutes"
            class="!w-full"
            :options="NOTICE_OPTIONS"
          />
        </label>
        <Input
          v-model.number="form.horizon_days"
          type="number"
          min="1"
          max="180"
          label="Agendar até (dias)"
        />
      </div>
    </section>

    <section class="grid gap-3" data-testid="national-holidays">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            Feriados nacionais
          </h4>
          <p class="mb-0 text-xs text-n-slate-11">
            Bloqueados por padrão. Clique em um feriado para atender nesse dia.
            Feriados estaduais e municipais entram em Datas bloqueadas.
          </p>
        </div>
        <Switch
          v-model="form.block_national_holidays"
          data-testid="holidays-toggle"
        />
      </div>
      <ul
        v-if="form.block_national_holidays && upcomingHolidays.length"
        class="m-0 flex list-none flex-wrap gap-2 p-0"
      >
        <li v-for="holiday in upcomingHolidays" :key="holiday.date">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-full border border-solid py-1 pl-2 pr-3 text-xs transition-colors"
            :class="
              isHolidayOpen(holiday)
                ? 'border-n-teal-6 bg-n-teal-2 text-n-teal-11'
                : 'border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
            "
            :aria-pressed="isHolidayOpen(holiday)"
            :title="
              isHolidayOpen(holiday)
                ? 'A empresa atende neste feriado'
                : 'Bloqueado para agendamentos'
            "
            :data-testid="`holiday-${holiday.date}`"
            @click="toggleHoliday(holiday)"
          >
            <span
              class="size-3.5 shrink-0"
              :class="
                isHolidayOpen(holiday)
                  ? 'i-hugeicons-checkmark-circle-02'
                  : 'i-hugeicons-calendar-remove-01 text-n-slate-11'
              "
              aria-hidden="true"
            />
            <span class="font-medium tabular-nums">
              {{ formatDate(holiday.date).slice(0, 5) }}
            </span>
            <span>{{ holiday.name }}</span>
            <span v-if="isHolidayOpen(holiday)" class="font-medium">
              · atende
            </span>
          </button>
        </li>
      </ul>
    </section>

    <section class="grid gap-3">
      <div>
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          Datas bloqueadas
        </h4>
        <p class="mb-0 text-xs text-n-slate-11">
          Feriados, férias ou dias sem atendimento. Escolha um dia ou um
          período.
        </p>
      </div>
      <DatePicker
        v-model:date-range="blockRange"
        v-model:range-type="rangeType"
        class="w-full min-w-0"
        teleport-to-body
        @date-range-changed="blockRange = $event.slice(0, 2)"
      >
        <template #trigger="{ toggle, isOpen }">
          <button
            type="button"
            class="flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-solid bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 transition-colors hover:border-n-slate-7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            :class="isOpen ? 'border-n-brand' : 'border-n-weak'"
            :aria-expanded="isOpen"
            aria-haspopup="dialog"
            data-testid="block-range-trigger"
            @click="toggle"
          >
            <span
              class="i-hugeicons-calendar-03 size-4 shrink-0 text-n-slate-11"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 truncate">{{ blockRangeLabel }}</span>
            <span
              class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
              aria-hidden="true"
            />
          </button>
        </template>
      </DatePicker>
      <div class="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Input
          v-model="blockReason"
          label="Motivo (opcional)"
          placeholder="Ex.: Feriado"
        />
        <Button
          type="button"
          color="slate"
          variant="faded"
          icon="i-hugeicons-calendar-remove-01"
          label="Bloquear"
          data-testid="block-dates"
          @click="addBlockedPeriod"
        />
      </div>
      <ul
        v-if="form.blocked_dates.length"
        class="m-0 flex list-none flex-wrap gap-2 p-0"
      >
        <li
          v-for="entry in form.blocked_dates"
          :key="entry.date"
          class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-1 py-1 pl-3 pr-1 text-xs text-n-slate-12"
        >
          <span>{{ formatDate(entry.date) }}</span>
          <span v-if="entry.reason" class="text-n-slate-11">
            · {{ entry.reason }}
          </span>
          <Button
            type="button"
            size="xs"
            color="slate"
            variant="ghost"
            icon="i-hugeicons-cancel-01"
            :aria-label="`Desbloquear ${formatDate(entry.date)}`"
            @click="removeBlockedDate(entry.date)"
          />
        </li>
      </ul>
      <p v-else class="mb-0 text-xs text-n-slate-11">Nenhuma data bloqueada.</p>
    </section>

    <div class="flex justify-end">
      <Button
        type="submit"
        :is-loading="isSaving"
        label="Salvar agenda da IA"
        data-testid="save-availability"
      />
    </div>
  </form>
</template>
